import api from './axios';
import { AxiosError } from 'axios';

// Backend response wrapper interface
interface BackendResponse<T> {
  data: T;
  message: string;
  success: boolean;
  timestamp: string;
}

export interface Feed {
  id: string;
  name: string;
  type: string;
  form: string;
  description?: string;
  quantityKg: number;
  unitPrice?: number;
  supplier?: string;
  batchNumber?: string;
  manufacturingDate?: string;
  expiryDate?: string;
  proteinContent?: number;
  fatContent?: number;
  fiberContent?: number;
  moistureContent?: number;
  recommendedFCR?: number;
  isActive: boolean;
  tenantId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFeedDto {
  name: string;
  type: string;
  form: string;
  description?: string;
  quantityKg: number;
  unitPrice?: number;
  supplier?: string;
  batchNumber?: string;
  manufacturingDate?: string;
  expiryDate?: string;
  proteinContent?: number;
  fatContent?: number;
  fiberContent?: number;
  moistureContent?: number;
  recommendedFCR?: number;
  isActive: boolean;
  tenantId?: string;
}

export interface FeedVendor {
  id: string;
  name: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  address?: string;
  website?: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateFeedDto extends Partial<CreateFeedDto> {}

// Type guard to check if an object is a Feed
function isFeed(obj: any): obj is Feed {
  return obj && typeof obj === 'object' && 'id' in obj && 'name' in obj && 'quantityKg' in obj;
}

export const fetchFeeds = async (): Promise<Feed[]> => {
  try {
    const response = await api.get<BackendResponse<Feed[]>>('/feeds');
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data && Array.isArray(responseData.data)) {
      return responseData.data;
    }
    
    // Fallback: check if the response is directly an array
    if (Array.isArray(responseData)) {
      return responseData;
    }
    
    console.error('Invalid feeds response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error fetching feeds:', error);
    throw error;
  }
};

export const fetchFeed = async (id: string): Promise<Feed> => {
  try {
    const response = await api.get<BackendResponse<Feed>>(`/feeds/${id}`);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a Feed object
    if (isFeed(responseData)) {
      return responseData;
    }
    
    console.error('Invalid feed response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error(`Error fetching feed ${id}:`, error);
    throw error;
  }
};

export const createFeed = async (data: CreateFeedDto): Promise<Feed> => {
  try {
    const response = await api.post<BackendResponse<Feed>>('/feeds', data);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a Feed object
    if (isFeed(responseData)) {
      return responseData;
    }
    
    console.error('Invalid create feed response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error creating feed:', error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const updateFeed = async (id: string, data: UpdateFeedDto): Promise<Feed> => {
  try {
    const response = await api.patch<BackendResponse<Feed>>(`/feeds/${id}`, data);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a Feed object
    if (isFeed(responseData)) {
      return responseData;
    }
    
    console.error('Invalid update feed response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error(`Error updating feed ${id}:`, error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const deleteFeed = async (id: string): Promise<void> => {
  try {
    await api.delete(`/feeds/${id}`);
  } catch (error) {
    console.error(`Error deleting feed ${id}:`, error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export interface FeedingTable {
  id: string;
  ageStart: number;
  ageEnd: number;
  feedNo?: string;
  totalFeedKg?: number;
  avgWeight?: number;
  feedPercent?: number;
  dailyFeed?: number;
  checkTrayFeed?: number;
  checkingTime?: number;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFeedingTableDto extends Omit<FeedingTable, 'id' | 'createdAt' | 'updatedAt' | 'tenantId'> {}
export interface UpdateFeedingTableDto extends Partial<CreateFeedingTableDto> {}

export const fetchFeedingTables = async (): Promise<FeedingTable[]> => {
  const response = await api.get<BackendResponse<FeedingTable[]>>('/feeds/feeding-tables');
  return response.data.data;
};

export const createFeedingTable = async (data: CreateFeedingTableDto): Promise<FeedingTable> => {
  const response = await api.post<BackendResponse<FeedingTable>>('/feeds/feeding-tables', data);
  return response.data.data;
};

export const updateFeedingTable = async (id: string, data: UpdateFeedingTableDto): Promise<FeedingTable> => {
  const response = await api.put<BackendResponse<FeedingTable>>(`/feeds/feeding-tables/${id}`, data);
  return response.data.data;
};

export const deleteFeedingTable = async (id: string): Promise<void> => {
  await api.delete(`/feeds/feeding-tables/${id}`);
};

// Feed Performance Template Names
export interface TemplateName {
  templateName: string;
}

export const fetchTemplateNames = async (): Promise<TemplateName[]> => {
  try {
    console.log('Fetching template names...');
    const response = await api.get<BackendResponse<TemplateName[]>>('/feed-performance/template-names');
    
    console.log('Template names response:', response);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    console.log('Template names response data:', responseData);
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data && Array.isArray(responseData.data)) {
      console.log('Returning wrapped data:', responseData.data);
      return responseData.data;
    }
    
    // Fallback: check if the response is directly an array
    if (Array.isArray(responseData)) {
      console.log('Returning direct array:', responseData);
      return responseData;
    }
    
    console.error('Invalid template names response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error fetching template names:', error);
    throw error;
  }
};

export const fetchTemplateByName = async (templateName: string): Promise<FeedPerformanceTemplate> => {
  try {
    const response = await api.get<BackendResponse<FeedPerformanceTemplate>>(`/feed-performance/templates/name/${encodeURIComponent(templateName)}`);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if the response is directly the template
    if (responseData && typeof responseData === 'object' && 'id' in responseData && 'templateName' in responseData) {
      return responseData as unknown as FeedPerformanceTemplate;
    }
    
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error fetching template by name:', error);
    throw error;
  }
};

// Feed Performance Template interfaces and functions
export interface FeedPerformanceTemplate {
  id: string;
  feedId: string;
  vendorId?: string;
  templateName: string;
  description?: string;
  isActive: boolean;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
  feed?: Feed;
  vendor?: FeedVendor;
  templateData?: FeedPerformanceTemplateData[];
}

export interface FeedPerformanceTemplateData {
  id: string;
  templateId: string;
  doc: number;
  abw?: number;
  fcrPercentage?: number;
  feedPercentage?: number;
  feed1?: number;
  feed2?: number;
  feed3?: number;
  feed4?: number;
  totalFeed?: number;
  cumulativeFeed?: number;
  biomass?: number;
  count?: number;
  finalSurvival?: number;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateTemplateDto {
  templateName?: string;
  description?: string;
  isActive?: boolean;
}

export interface UpdateTemplateDataDto {
  doc?: number;
  abw?: number;
  fcrPercentage?: number;
  feedPercentage?: number;
  feed1?: number;
  feed2?: number;
  feed3?: number;
  feed4?: number;
  totalFeed?: number;
  cumulativeFeed?: number;
  biomass?: number;
  count?: number;
  finalSurvival?: number;
}

export const fetchAllTemplates = async (): Promise<FeedPerformanceTemplate[]> => {
  try {
    const response = await api.get<BackendResponse<FeedPerformanceTemplate[]>>('/feed-performance/templates');
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data && Array.isArray(responseData.data)) {
      return responseData.data;
    }
    
    // Fallback: check if the response is directly an array
    if (Array.isArray(responseData)) {
      return responseData;
    }
    
    console.error('Invalid templates response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error fetching templates:', error);
    throw error;
  }
};

export const fetchTemplateById = async (id: string): Promise<FeedPerformanceTemplate> => {
  try {
    const response = await api.get<BackendResponse<FeedPerformanceTemplate>>(`/feed-performance/templates/${id}`);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a template object
    if (responseData && typeof responseData === 'object' && 'id' in responseData) {
      return responseData as unknown as FeedPerformanceTemplate;
    }
    
    console.error('Invalid template response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error(`Error fetching template ${id}:`, error);
    throw error;
  }
};

export const updateTemplate = async (id: string, data: UpdateTemplateDto): Promise<FeedPerformanceTemplate> => {
  try {
    const response = await api.patch<BackendResponse<FeedPerformanceTemplate>>(`/feed-performance/templates/${id}`, data);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a template object
    if (responseData && typeof responseData === 'object' && 'id' in responseData) {
      return responseData as unknown as FeedPerformanceTemplate;
    }
    
    console.error('Invalid update template response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error(`Error updating template ${id}:`, error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const updateTemplateData = async (id: string, templateData: UpdateTemplateDataDto[]): Promise<FeedPerformanceTemplate> => {
  try {
    const response = await api.patch<BackendResponse<FeedPerformanceTemplate>>(`/feed-performance/templates/${id}/data`, templateData);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a template object
    if (responseData && typeof responseData === 'object' && 'id' in responseData) {
      return responseData as unknown as FeedPerformanceTemplate;
    }
    
    console.error('Invalid update template data response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error(`Error updating template data ${id}:`, error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const deleteTemplate = async (id: string): Promise<void> => {
  try {
    await api.delete(`/feed-performance/templates/${id}`);
  } catch (error) {
    console.error(`Error deleting template ${id}:`, error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const deleteTemplatesByFeedId = async (feedId: string): Promise<void> => {
  try {
    await api.delete(`/feed-performance/templates/feed/${feedId}`);
  } catch (error) {
    console.error(`Error deleting templates for feed ${feedId}:`, error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const fetchTemplatesByVendor = async (): Promise<any[]> => {
  try {
    const response = await api.get<BackendResponse<any[]>>('/feed-performance/templates-by-vendor');
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data && Array.isArray(responseData.data)) {
      return responseData.data;
    }
    
    // Fallback: check if the response is directly an array
    if (Array.isArray(responseData)) {
      return responseData;
    }
    
    console.error('Invalid templates by vendor response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error fetching templates by vendor:', error);
    throw error;
  }
};

// Upload template function
export const uploadTemplate = async (
  file: File,
  feedId: string,
  vendorId: string,
  templateName: string
): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('feedId', feedId);
    formData.append('vendorId', vendorId);
    formData.append('templateName', templateName);

    const response = await api.post<BackendResponse<any>>('/feed-performance/upload-template', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 45000, // 45 seconds timeout (under Cloudflare's 60s limit)
    });

    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: return the response data directly
    return responseData;
  } catch (error) {
    console.error('Error uploading template:', error);
    if (error instanceof AxiosError) {
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        throw new Error('Upload timed out. The file may be too large or processing is taking too long. Please try with a smaller file or contact support.');
      }
      if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
      }
    }
    throw error;
  }
};
